<template>
  <component-wrapper class="account-activation-fi">
    <grid-container-fi v-if="isMounted && !isLoading && fields">
      <div
        class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12 lg:fi-col-start-4 lg:fi-col-end-10"
      >
        <result-status-fi
          :error="errorMsg"
          :headline="fields.headline"
          :custom-icon="existingUserActivation ? 'mail' : 'check'"
        >
          <template v-if="existingUserActivation">
            <block-notification-fi
              v-if="exisitingUserSuccessText"
              type="success"
              class="fi-mb-30"
              icon="check"
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p v-html="exisitingUserSuccessText" />
            </block-notification-fi>
          </template>
          <template v-else>
            <div class="account-activation-fi__actions">
              <template v-if="!isEditMode">
                <button-fi
                  v-if="fields.buttonPrimary?.value"
                  class="button-fi__primary--large button-fi__xs-streched"
                  :href="fields.buttonPrimary.value.url || fields.buttonPrimary.value.href"
                  natural-events
                >
                  {{ fields.buttonPrimary.value.text }}
                </button-fi>
                <button-fi
                  v-if="fields.buttonTertiary?.value?.url || fields.buttonTertiary?.value?.href"
                  class="button-fi__tertiary--large button-fi__xs-streched"
                  :href="fields.buttonTertiary.value.url || fields.buttonTertiary.value.href"
                  natural-events
                >
                  {{ fields.buttonTertiary.value.text }}
                </button-fi>
              </template>
              <template v-else>
                <!-- eslint-disable vue/no-v-html -->
                <span
                  v-if="fields.buttonPrimary?.editable"
                  class="button-fi__primary--large"
                  v-html="fields.buttonPrimary.editable"
                />
                <span
                  v-if="fields.buttonTertiary?.editable"
                  class="button-fi__tertiary--large"
                  v-html="fields.buttonTertiary.editable"
                />
                <!-- eslint-enable vue/no-v-html -->
              </template>
            </div>
          </template>
        </result-status-fi>
      </div>
    </grid-container-fi>

    <loading-indicator
      v-else
      variant="spinnerLocal"
    />
  </component-wrapper>
</template>

<script setup lang="ts">
import { useClientHandle, useMutation } from '@urql/vue';
import { ButtonFi, LoadingIndicator } from 'atoms';
import { computed, onMounted, ref } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import BlockNotificationFi from '@/components/notifications-fi/block-notification-fi/BlockNotificationFi.vue';
import ResultStatusFi from '@/components/result-status-fi/ResultStatusFi.vue';
import { useCookies } from '@/composables/cookies';
import { useEventHub } from '@/composables/eventHub';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import type {
  ActivateUserMutation,
  ActivateUserMutationVariables,
  CheckCidActivatedQuery,
  CheckCidActivatedQueryVariables,
} from '@/generated/graphql';
import ACTIVATE_USER from '@/graphql/activateUser.gql';
import CHECK_CID_ACTIVATED from '@/graphql/checkCidActivated.gql';

defineOptions({ name: 'AccountActivationFi' });

const fields = useFields('AccountActivationFiFields');

const fischer = useFischer();
const { sendEvent } = useEventHub();
const { cookies } = useCookies();
const handle = useClientHandle();
const { executeMutation: activateUserMutate } = useMutation<
  ActivateUserMutation,
  ActivateUserMutationVariables
>(ACTIVATE_USER);

const isMounted = ref(false);
const error = ref(false);
const errorMsg = ref<{ title: string; message: string } | undefined>();
const isLoading = ref(true);
const token = ref('');

const existingUserActivation = computed(() => Boolean(fields.existingUserActivation?.value));

const secureUid = computed(
  () =>
    fischer.query('uid')?.replace(/[^a-zA-Z0-9 _-]/g, (x: string) => `&#${x.charCodeAt(0)};`) ??
    null,
);

const exisitingUserSuccessText = computed(() => {
  const text = fields.existingUserSuccess?.value;
  if (!text) return null;
  return secureUid.value
    ? text.replace('{0}', `<strong>${secureUid.value}</strong>`)
    : text.replace('{0}', `<strong>${fields.existingUserSuccessPlaceholder?.value}</strong>`);
});

const handleError = () => {
  isLoading.value = false;

  if (IS_SHOWROOM) {
    return;
  }

  error.value = true;
  errorMsg.value = {
    title: fields.errorHeadline?.value ?? '',
    message: fields.errorDescription?.value ?? '',
  };
};

const getToken = () => {
  const accessToken = fischer.query('token');
  if (accessToken?.length) {
    error.value = false;
    token.value = accessToken;
  } else {
    handleError();
  }
};

const activateToken = async () => {
  if (cookies.get('debug-stop-activation')) {
    console.debug('stopping activation as per cookie');
    return;
  }
  isLoading.value = true;

  try {
    const res = await activateUserMutate({
      baseSiteId: fischer.baseSiteId(),
      token: token.value,
      lang: fischer.language_639_1(),
      division: fischer.user.division(),
      distributionChannel: fischer.user.distributionChannel(),
    });

    if (res.error) {
      console.error(res.error);
      handleError();
      return;
    }

    isLoading.value = false;

    if (res.data?.activateUser?.success) {
      error.value = false;
      sendEvent('custom', {}, 'account_activated');
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'myfischerRegistrationCompleted' });
    } else {
      handleError();
    }
  } catch (err) {
    console.error(err);
    handleError();
  }
};

const checkIfAlreadyActivated = async (cid: string) => {
  isLoading.value = true;
  try {
    const { data: cidData, error: cidError } = await handle.client
      .query<
        CheckCidActivatedQuery,
        CheckCidActivatedQueryVariables
      >(CHECK_CID_ACTIVATED, { baseSiteId: fischer.baseSiteId(), cid }, { requestPolicy: 'network-only' })
      .toPromise();
    if (cidError) {
      console.error(cidError);
      handleError();
      return;
    }
    if (cidData?.checkCidActivated === true) {
      isLoading.value = false;
    } else {
      await activateToken();
    }
  } catch (err) {
    console.error(err);
    handleError();
  }
};

onMounted(() => {
  isMounted.value = true;

  if (fischer.app.isEditMode()) {
    isLoading.value = false;
    return;
  }

  getToken();

  if (!error.value && token.value) {
    const cid = fischer.query('cid');
    if (cid) {
      checkIfAlreadyActivated(cid);
    } else {
      activateToken();
    }
  }
});

// expose for tests
defineExpose({ isLoading, error });
</script>

<style scoped src="./account-activation-fi" />
